import { formatServiceLabel, heatingEnergyLabels, pseEnergyTypeLabels } from "./benefit-enums"
import { enumLabel } from "./benefit-format"
import type { EnergyServiceOption } from "./energy-intake"
import type { BenefitDetailDto, ProvidedServiceDto } from "./benefit-types"

export interface ProgramHeatingSourceDisplay {
  pseEnergyType: string
  liheapPrimaryHeatingService: string
  liheapSecondaryHeatingService: string
  sheapHeatSource: string
  liheapPrimaryServiceProvider: string | null
}

type SavedFacts = Record<string, unknown>
type SavedBlock = Record<string, unknown>

function parseSavedFacts(value: string | null | undefined): SavedFacts | null {
  if (!value) return null
  try {
    const parsed: unknown = JSON.parse(value)
    return parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed as SavedFacts
      : null
  } catch {
    return null
  }
}

function savedBlock(facts: SavedFacts | null, name: string): SavedBlock | null {
  const value = facts?.[name]
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as SavedBlock
    : null
}

function savedValue(block: SavedBlock | null, name: string): { present: boolean; value: unknown } {
  if (!block || !Object.prototype.hasOwnProperty.call(block, name)) return { present: false, value: undefined }
  return { present: true, value: block[name] }
}

function isUnanswered(value: unknown): boolean {
  return value === null || value === undefined || (typeof value === "string" && value.trim() === "")
}

function formatPseEnergyType(value: unknown): string {
  if (isUnanswered(value)) return "—"
  if (typeof value === "string") {
    const trimmed = value.trim()
    const numeric = Number(trimmed)
    if (Number.isInteger(numeric) && trimmed !== "") return enumLabel(pseEnergyTypeLabels, numeric)
    return enumLabel(pseEnergyTypeLabels, trimmed)
  }
  return enumLabel(pseEnergyTypeLabels, value as number)
}

function formatHeatingService(
  service: ProvidedServiceDto | null | undefined,
  providerNames: Record<string, string>,
): { label: string; provider: string | null } {
  if (!service) return { label: "—", provider: null }
  const provider = providerNames[String(service.provider)] ?? `Provider ${service.provider}`
  return { label: `${formatServiceLabel(service.service)} (${provider})`, provider }
}

function formatSavedHeatingService(
  value: unknown,
  options: readonly EnergyServiceOption[],
): { label: string; provider: string | null } {
  if (isUnanswered(value)) return { label: "—", provider: null }
  const id = typeof value === "number" ? value : Number(value)
  const option = Number.isInteger(id) ? options.find(candidate => candidate.id === id) : undefined
  // A saved selector is authoritative. Until the reference endpoint resolves it, do not fall
  // back to an older legacy row or invent a service/provider label from the numeric ID.
  if (!option || !option.providerName.trim() || !option.serviceName.trim()) return { label: "—", provider: null }
  return {
    label: `${option.serviceName.trim()} (${option.providerName.trim()})`,
    provider: option.providerName.trim(),
  }
}

/**
 * Keeps each program's heating fact in its own display slot.
 *
 * A Combined application can have a PSE energy type and different LIHEAP primary and
 * secondary heating services at the same time. Saved aggregate evidence is newer than
 * legacy projections: a present property wins even when its value is explicitly null.
 */
export function programHeatingSourceDisplay(
  benefit: Pick<BenefitDetailDto, "pseBenefitDetails" | "liheapBenefitDetails" | "sheapBenefitDetails" | "energyIntakeDraftJson">,
  providerNames: Record<string, string>,
  energyServiceOptions: readonly EnergyServiceOption[] = [],
): ProgramHeatingSourceDisplay {
  const facts = parseSavedFacts(benefit.energyIntakeDraftJson)
  const pseEvidence = savedValue(savedBlock(facts, "pse"), "energyType")
  const liheapBlock = savedBlock(facts, "liheap")
  const liheapPrimaryEvidence = savedValue(liheapBlock, "primaryServiceProviderServiceId")
  const liheapSecondaryEvidence = savedValue(liheapBlock, "secondaryServiceProviderServiceId")
  const sheapEvidence = savedValue(savedBlock(facts, "sheap"), "heatSource")

  const pseEnergyType = pseEvidence.present
    ? formatPseEnergyType(pseEvidence.value)
    : benefit.pseBenefitDetails?.energyType == null
      ? "—"
      : formatPseEnergyType(benefit.pseBenefitDetails.energyType)

  const legacyPrimary = formatHeatingService(
    benefit.liheapBenefitDetails?.primaryHeatingService,
    providerNames,
  )
  const legacySecondary = formatHeatingService(
    benefit.liheapBenefitDetails?.secondaryHeatingService,
    providerNames,
  )
  const liheapPrimary = liheapPrimaryEvidence.present
    ? formatSavedHeatingService(liheapPrimaryEvidence.value, energyServiceOptions)
    : legacyPrimary
  const liheapSecondary = liheapSecondaryEvidence.present
    ? formatSavedHeatingService(liheapSecondaryEvidence.value, energyServiceOptions)
    : legacySecondary
  const sheapHeatSource = sheapEvidence.present
    ? (isUnanswered(sheapEvidence.value) ? "—" : enumLabel(heatingEnergyLabels, sheapEvidence.value as number | string | { value: number }))
    : benefit.sheapBenefitDetails?.heatSource == null
      ? "—"
      : enumLabel(heatingEnergyLabels, benefit.sheapBenefitDetails.heatSource)

  return {
    pseEnergyType,
    liheapPrimaryHeatingService: liheapPrimary.label,
    liheapSecondaryHeatingService: liheapSecondary.label,
    sheapHeatSource,
    liheapPrimaryServiceProvider: liheapPrimary.provider,
  }
}
